import { FCC, useFormatDate } from "@dgs/core";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import {
	BaseTravelSegment,
	BaseTravelSegmentCar,
	BaseTravelSegmentFlight,
	BaseTravelSegmentShuttle,
	BaseTravelSegmentTrain,
	TravelDirection,
} from "../../../../shared/types/travelSegment";
import { travelSegmentIdentifier } from "../travelSegmentIdentifier";
import { TwoColumnCard } from "./Card";
import { LabeledValue } from "./LabeledValue";

interface Props<T extends BaseTravelSegment> {
	travelSegment: T;
	direction?: TravelDirection;
}

interface DateTimeLabeledValueProps {
	date: string;
	time: string;
}

export const DateTimeLabeledValue: FCC<DateTimeLabeledValueProps> = ({ date, time, children }) => {
	const formatDate = useFormatDate();

	return (
		<div>
			<div>
				{formatDate(date, { dateStyle: "short" })}, {time}
			</div>
			<strong>{children}</strong>
		</div>
	);
};

export const TravelSegmentSummaryCard: FC<Props<BaseTravelSegment>> = ({ travelSegment, direction }) => {
	switch (travelSegment.type) {
		case "car":
			return <TravelSegmentCarCard travelSegment={travelSegment} direction={direction} />;
		case "flight":
			return <TravelSegmentFlightCard travelSegment={travelSegment} />;
		case "train":
			return <TravelSegmentTrainCard travelSegment={travelSegment} />;
		case "shuttle":
			return <TravelSegmentShuttleCard travelSegment={travelSegment} />;
	}
};
const TravelSegmentCarCard: FC<Props<BaseTravelSegmentCar>> = ({ travelSegment, direction }) => {
	const formatDate = useFormatDate();
	const { t } = useTranslation();

	return (
		<TwoColumnCard>
			<LabeledValue label={direction === "arrival" ? t("Arrival time") : t("Departure time")}>
				{formatDate(direction === "arrival" ? travelSegment.arrivalDate : travelSegment.departureDate, {
					dateStyle: "short",
				})}
				,{direction === "arrival" ? travelSegment.arrivalTime : travelSegment.departureTime}
			</LabeledValue>
			<LabeledValue label={t("License plate")}>{travelSegmentIdentifier(travelSegment)}</LabeledValue>
			<LabeledValue label={t("Departure location")}>{travelSegment.departureLocation || "-"}</LabeledValue>
			<LabeledValue label={t("Arrival location")}>{travelSegment.arrivalLocation || "-"}</LabeledValue>
			<LabeledValue label={t("Parking spot")}>{travelSegment.parkingSpot || "-"}</LabeledValue>
			<LabeledValue label={t("Charging station")}>{travelSegment.chargingStation || "-"}</LabeledValue>
		</TwoColumnCard>
	);
};
const TravelSegmentTrainCard: FC<Props<BaseTravelSegmentTrain>> = ({ travelSegment }) => {
	const { t } = useTranslation();

	return (
		<TwoColumnCard>
			<DateTimeLabeledValue date={travelSegment.departureDate} time={travelSegment.departureTime}>
				{travelSegment.departureLocation}
			</DateTimeLabeledValue>
			<DateTimeLabeledValue date={travelSegment.arrivalDate} time={travelSegment.arrivalTime}>
				{travelSegment.arrivalLocation}
			</DateTimeLabeledValue>
			<LabeledValue label={t("Train number")}>{travelSegmentIdentifier(travelSegment)}</LabeledValue>
			<LabeledValue label={t("Service class")}>{travelSegment.serviceClass || "-"}</LabeledValue>
		</TwoColumnCard>
	);
};
const TravelSegmentFlightCard: FC<Props<BaseTravelSegmentFlight>> = ({ travelSegment }) => {
	const { t } = useTranslation();

	return (
		<TwoColumnCard>
			<DateTimeLabeledValue date={travelSegment.departureDate} time={travelSegment.departureTime}>
				({travelSegment.departureLocation?.iataCode}) {travelSegment.departureLocation?.name}
			</DateTimeLabeledValue>
			<DateTimeLabeledValue date={travelSegment.arrivalDate} time={travelSegment.arrivalTime}>
				({travelSegment.arrivalLocation?.iataCode}) {travelSegment.arrivalLocation?.name}
			</DateTimeLabeledValue>
			<LabeledValue label={t("Flight number")}>{travelSegmentIdentifier(travelSegment)}</LabeledValue>
			<LabeledValue label={t("Service class")}>{travelSegment.serviceClass || "-"}</LabeledValue>
		</TwoColumnCard>
	);
};
const TravelSegmentShuttleCard: FC<Props<BaseTravelSegmentShuttle>> = ({ travelSegment }) => {
	const { t } = useTranslation();

	return (
		<TwoColumnCard>
			<DateTimeLabeledValue date={travelSegment.departureDate} time={travelSegment.departureTime}>
				{travelSegment.departureLocation}
			</DateTimeLabeledValue>
			<LabeledValue label={t("Name")}>{travelSegmentIdentifier(travelSegment)}</LabeledValue>
			<DateTimeLabeledValue date={travelSegment.arrivalDate} time={travelSegment.arrivalTime}>
				{travelSegment.arrivalLocation}
			</DateTimeLabeledValue>
		</TwoColumnCard>
	);
};
